import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ClipboardService, IconName, TooltipDirective } from '@platform-frontend/ui-tools';
import { Observable, of } from 'rxjs';
import { MARKETPLACE_MATOMO_EVENT_CATEGORY_AFTER_SALES } from '../../marketplace-matomo-events';
import { MARKETPLACE_TRANSLOCO_SCOPE } from '../../marketplace-transloco-scope';
import { ProductPath } from '../model/product-path';

class DownloadContainer {
  type: string = 'CONTAINER';
  productPaths: ProductPath[];
}

@UntilDestroy()
@Component({
  selector: 'yat-download-button-group',
  templateUrl: './download-button-group.component.html',
  styleUrls: ['./download-button-group.component.scss']
})
export class DownloadButtonGroupComponent implements OnChanges {
  constructor(private clipboardService: ClipboardService, private translocoService: TranslocoService) {}

  @Input()
  productPaths: ProductPath[] | null;

  @Input()
  showDownloadOverviewPanel: boolean = false;

  @Input()
  productName: string = '';

  @ViewChild(TooltipDirective)
  tooltip: TooltipDirective;

  packedProductPaths: (ProductPath | DownloadContainer)[] | null = null;
  readonly MATOMO_EVENT_CATEGORY_AFTER_SALES = MARKETPLACE_MATOMO_EVENT_CATEGORY_AFTER_SALES;

  ngOnChanges(): void {
    if (!this.productPaths) return;
    const index = this.productPaths.findIndex(path => path.type === 'DOWNLOAD');
    if (index > -1) {
      const result = this.productPaths.filter(path => path.type !== 'DOWNLOAD') as (ProductPath | DownloadContainer)[];
      const container = new DownloadContainer();
      container.productPaths = this.productPaths.filter(path => path.type === 'DOWNLOAD');
      result.splice(index, 0, container);
      this.packedProductPaths = result;
    } else {
      this.packedProductPaths = this.productPaths;
    }
  }

  getProductPaths(path: ProductPath | DownloadContainer): ProductPath[] {
    switch (path.type) {
      case 'CONTAINER':
        return path.productPaths;
    }
    return [path as ProductPath];
  }

  getUrl(path: ProductPath | DownloadContainer): string {
    switch (path.type) {
      case 'DOWNLOAD':
      case 'DOWNLOAD_PAGE':
      case 'YATTA_PROFILES':
      case 'ECLIPSE_UPDATE_SITE':
        return (path as ProductPath).url;
    }
    return '';
  }

  getDescription(path: ProductPath | DownloadContainer): Observable<string> {
    switch (path.type) {
      case 'DOWNLOAD':
        if ((path as ProductPath).description) return of((path as ProductPath).description ?? '');
        switch ((path as ProductPath).os) {
          case 'WIN':
            return this.translocoService.selectTranslate('product.downloadButtonGroup.descriptions.win', {}, MARKETPLACE_TRANSLOCO_SCOPE);
          case 'OSX':
            return this.translocoService.selectTranslate('product.downloadButtonGroup.descriptions.osx', {}, MARKETPLACE_TRANSLOCO_SCOPE);
          case 'LINUX':
            return this.translocoService.selectTranslate('product.downloadButtonGroup.descriptions.linux', {}, MARKETPLACE_TRANSLOCO_SCOPE);
        }
        break;
      case 'YATTA_PROFILES':
      case 'ECLIPSE_UPDATE_SITE':
        return this.translocoService.selectTranslate(
          'product.downloadButtonGroup.descriptions.eclipseUpdateSite',
          { description: (path as ProductPath).description },
          MARKETPLACE_TRANSLOCO_SCOPE
        );
    }
    return of('');
  }

  getIcon(path: ProductPath | DownloadContainer): IconName | null {
    switch (path.type) {
      case 'DOWNLOAD':
        switch ((path as ProductPath).os) {
          case 'WIN':
            return 'windows';
          case 'OSX':
            return 'apple';
          case 'LINUX':
            return 'linux';
        }
        break;
      case 'YATTA_PROFILES':
        return 'launcher-branded';
      case 'ECLIPSE_UPDATE_SITE':
        return 'eclipse-branded';
    }
    return null;
  }
}
